import type { ReactElement, ReactNode } from 'react';

import { cn } from '@/components/ui/cn';

import { deviationTone, formatHours, formatPct, rangeProblem } from '../presentation';
import type { TimeStudyFilters } from '../types';
import { useDeviation, useReworkSummary } from '../use-time-study';

// Indicadores de Análisis (so=420, spec §4.2 tarjeta 1): trabajos terminados, esfuerzo
// real frente a estándar, reprocesos y trabajos sobre el estándar, de los totales de
// desviación y reprocesos. Cada uno aparece con su permiso; mientras carga, un guion.

interface Props {
  filters: TimeStudyFilters;
  companyId: string;
  canDeviation: boolean;
  canRework: boolean;
}

const TONE_CLASS = { ok: 'ok', warn: 'warn', alert: 'red', none: '' } as const;
const DASH = '—';

function Tile(props: { k: string; v: ReactNode; d: string; tone?: string }): ReactElement {
  return (
    <div className={cn('tile', props.tone)} data-testid="kpi">
      <div className="k">{props.k}</div>
      <div className="v">{props.v}</div>
      <div className="d">{props.d}</div>
    </div>
  );
}

export function KpiTiles({ filters, companyId, canDeviation, canRework }: Props): ReactElement {
  const valid = rangeProblem(filters) === null;
  const dev = useDeviation('stage', filters, { companyId, enabled: valid && canDeviation }).data;
  const rework = useReworkSummary(filters, { companyId, enabled: valid && canRework }).data;
  const t = dev?.totals;
  const tiles: ReactElement[] = [];
  if (canDeviation) {
    const hasStd = t !== undefined && t.standardSeconds !== null;
    tiles.push(
      <Tile
        key="done"
        k="Trabajos terminados"
        v={t?.workCount ?? DASH}
        d="con aprobación en el rango"
      />,
      <Tile
        key="effort"
        k="Esfuerzo real"
        tone={TONE_CLASS[deviationTone(t?.deviationPct ?? null)]}
        v={
          t === undefined
            ? DASH
            : formatHours(hasStd ? t.effortWithStandardSeconds : t.effortSeconds)
        }
        d={
          t === undefined
            ? 'frente al estándar'
            : hasStd
              ? `estándar ${formatHours(t.standardSeconds ?? 0)} · ${formatPct(t.deviationPct)}`
              : 'sin estándar para comparar'
        }
      />,
    );
  }
  if (canRework) {
    tiles.push(
      <Tile
        key="rework"
        k="Reprocesos"
        tone="warn"
        v={rework?.totals.reworkCount ?? DASH}
        d={rework ? `con ${formatHours(rework.totals.effortSeconds)} de retrabajo` : 'devoluciones'}
      />,
    );
  }
  if (canDeviation) {
    tiles.push(
      <Tile
        key="over"
        k="Sobre el estándar"
        tone="red"
        v={t?.overStandardCount ?? DASH}
        d={
          t === undefined
            ? 'trabajos'
            : `de ${t.workCount} trabajos · ${t.withoutStandardCount} sin estándar`
        }
      />,
    );
  }
  return (
    <div
      className={cn('tiles rise d1', tiles.length === 3 && 'cols-3')}
      aria-busy={valid && t === undefined && canDeviation}
    >
      {tiles}
    </div>
  );
}
